import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { StyleSheet, Text, View } from 'react-native'

import { PondCageOption } from './PondCageOption'
import { Mortality } from '../../types'
import { TypesOfMortality } from '../TypesOfMortality'
import { Separator } from '../../../../components/Separator'
import { InitialInputVoiceSearch } from '../../../../components/VoiceSearch/InitialInput/InitialInput'
import { FreshWaterContext } from '../../../../context/FreshWater/FreshWaterContext'
import { FormatPond } from '../../../../context/FreshWater/types'
import { corporateColor } from '../../../../theme/colors'
import { SeaWaterContext } from '../../../../context/SeaWater/SeaWaterContext'
import { AuthContext } from '../../../../context/Auth/AuthContext'
import { MoratlityInfo, MortalityItem } from '../../../../types'
import { useTranslation } from 'react-i18next'
import { AreaSelected } from '../../../../context/Auth/AuthReducer'

interface Props {
  parentId: number
  date: string
  onChange: (values: MoratlityInfo[]) => void
  initValues?: MoratlityInfo[]
}

export const PondsCage = ({ parentId, onChange, date, initValues }: Props) => {
  const {
    user: { areaSelected },
  } = useContext(AuthContext)

  const { ponds } = useContext(FreshWaterContext)
  const { cages } = useContext(SeaWaterContext)

  const dataMap: Partial<Record<Exclude<AreaSelected, null>, { [key: number]: FormatPond[] }>> =
    useMemo(
      () => ({
        sweetWater: ponds,
        seaWater: cages,
      }),
      [],
    )

  const { t } = useTranslation()
  const [typesOfMoratalitySelected, setTypesOfMoratalitySelected] = useState<MortalityItem>({})
  const [search, setSearch] = useState('')
  const [filteredData, setFilteredData] = useState<FormatPond[]>([])
  const [showTypesOfMoratlity, setShowTypesOfMortality] = useState(false)
  const [mortalityValues, setMortalityValues] = useState<{ [key: string]: number }>({})

  const levelThreeInfo = useRef<{
    levelThreeId: null | number
    levelThreeName: null | string
  }>({
    levelThreeId: null,
    levelThreeName: null,
  })

  useEffect(() => {
    const data = dataMap[areaSelected!]
    if (data) {
      setFilteredData(
        data[parentId]!.filter((pond) => pond.name.toLowerCase().includes(search.toLowerCase())),
      )
    }
  }, [search, parentId])

  useEffect(
    () => onChange(handleFinishData({ typesOfMoratalitySelected, mortalityValues })),
    [typesOfMoratalitySelected, mortalityValues],
  )

  useEffect(() => {
    if (!initValues) return

    setTypesOfMoratalitySelected(() => {
      return Object.fromEntries(
        initValues.map((value) => [
          `${value.levelThreeId}-${value.date}-${value.mortalityTypeId}`,
          { ...value },
        ]),
      )
    })
  }, [initValues])

  const getSearchValue = useCallback((value: string) => setSearch(value), [])

  const getValues = useCallback((values: { [key: string]: number }) => {
    setMortalityValues((prev) => ({ ...prev, ...values }))
  }, [])

  const handleShowTypesOfMoratalityCallback = useCallback(
    (values: { levelThreeId: number; levelThreeName: string }) => {
      setShowTypesOfMortality(true)
      levelThreeInfo.current = values
    },
    [],
  )

  const closeModal = useCallback(() => {
    setShowTypesOfMortality(false)
  }, [])

  const handleTypesOfMortalitySelected = useCallback(
    (values: MortalityItem) => {
      setTypesOfMoratalitySelected((prev) => {
        const { levelThreeId } = levelThreeInfo.current
        const name = `${levelThreeId!}-${date}`
        const newKeys = Object.keys(values).filter((key) => key.includes(name))
        const oldKeys = Object.keys(prev).filter((key) => key.includes(name))

        const keysToAdd = newKeys.filter((key) => !oldKeys.includes(key))
        const keysToRemove = oldKeys.filter((key) => !newKeys.includes(key))
        const updatedState: MortalityItem = { ...prev }

        keysToAdd.forEach((key) => {
          updatedState[key] = values[key]!
        })

        keysToRemove.forEach((key) => {
          delete updatedState[key]
        })

        return updatedState
      })
    },
    [date],
  )

  const handleFinishData = ({
    typesOfMoratalitySelected,
    mortalityValues,
  }: Mortality): MoratlityInfo[] =>
    Object.entries(typesOfMoratalitySelected).map(([key, item]): MoratlityInfo => {
      const amountOfMortality = mortalityValues[key] ?? 0
      return {
        ...item,
        quantity: amountOfMortality,
        unitId: item.levelThreeId,
      }
    })

  return (
    <View style={{ flexGrow: 10 }}>
      <InitialInputVoiceSearch
        placeholder={t(`BUSCAR ${areaSelected === 'sweetWater' ? 'ESTANQUE' : 'JAULA'}`)}
        getSearchValue={getSearchValue}
        hasInput
      />

      <View style={styles.header}>
        <View style={{ flex: 0.9 }}>
          <Text style={styles.headerText}>
            {areaSelected === 'sweetWater' ? t('ESTANQUE') : t('JAULA')}
          </Text>
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.headerText}>{t('TIPO DE MORTALIDAD')}</Text>
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.headerText}>{t('N° DE MORTALIDAD')}</Text>
        </View>
      </View>

      <Separator />
      {filteredData.map((item, index) => (
        <PondCageOption
          getValues={getValues}
          item={item}
          isLastItem={index + 1 === filteredData.length}
          key={item.id}
          name={`${item.id}-${date}`}
          date={date}
          typesOfMoratality={typesOfMoratalitySelected}
          mortalityValues={mortalityValues}
          showTypesOfMoratlity={handleShowTypesOfMoratalityCallback}
        />
      ))}
      {showTypesOfMoratlity && (
        <TypesOfMortality
          initialValues={typesOfMoratalitySelected}
          date={date}
          name={`${levelThreeInfo.current.levelThreeId!}-${date}`}
          levelThreeId={levelThreeInfo.current.levelThreeId!}
          levelThreeName={levelThreeInfo.current.levelThreeName!}
          closeModal={closeModal}
          onSelectedItem={handleTypesOfMortalitySelected}
        />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
  },
  headerText: {
    color: corporateColor,
    fontWeight: 'bold',
  },
})
